import type { SiteChatConfig, SiteChatRule, SiteChatTrigger } from '../types';

/**
 * Whether the schedule says someone answers now. Mirrors `SiteWidgetConfig.IsOnline` on the server: the
 * server decides whether a message needs the form, the widget decides what to draw, and both must agree.
 */
export function isOnlineNow(config: SiteChatConfig, now: Date): boolean {
    if (config.alwaysOnline !== false)
        return true;

    let parts: Intl.DateTimeFormatPart[];
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone: config.timezone || 'UTC',
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(now);
    } catch {
        parts = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
    }
    const weekday = parts.find(part => part.type === 'weekday')?.value ?? 'Mon';
    const hour = Number(parts.find(part => part.type === 'hour')?.value ?? 0) % 24;
    const minute = Number(parts.find(part => part.type === 'minute')?.value ?? 0);
    const dayIndex = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(weekday);
    const day = config.days?.[dayIndex];
    if (!day)
        return true;
    if (!day.on)
        return false;

    const from = toMinutes(day.from);
    const to = toMinutes(day.to);
    if (from == null || to == null)
        return true;
    const current = hour * 60 + minute;
    return to > from ? current >= from && current < to : current >= from || current < to;
}

function toMinutes(value: string | undefined) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value ?? '');
    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

export function isFormRequired(config: SiteChatConfig, online: boolean) {
    if (config.formEnabled === false || !config.fields.some(field => field.enabled))
        return false;
    return online ? config.formRequiredOnline : config.formRequiredOffline;
}

/** A URL rule: `value` is compared with the page address (lowercase, without the hash). */
export function matchesRule(rule: SiteChatRule, url: string) {
    const value = (rule.value ?? '').trim().toLowerCase();
    if (!value)
        return true;
    const address = url.toLowerCase().split('#')[0]!;
    const path = safePath(address);
    switch (rule.op) {
        case 'equals': return address === value || path === value || address.replace(/\/$/, '') === value.replace(/\/$/, '');
        case 'not_equals': return !(address === value || path === value || address.replace(/\/$/, '') === value.replace(/\/$/, ''));
        case 'not_contains': return !address.includes(value);
        default: return address.includes(value);
    }
}

function safePath(url: string) {
    try {
        return new URL(url).pathname;
    } catch {
        return url;
    }
}

/**
 * «Where to show»: every rule must hold (rules are ANDed, like a trigger's conditions), and the device
 * switches apply on top. An empty list shows the widget on every page carrying the snippet.
 */
export function isShownOn(config: SiteChatConfig, url: string, phone: boolean) {
    if (config.mobileOnly && !phone)
        return false;
    if (config.hideMobile && phone)
        return false;
    return (config.rules ?? []).every(rule => matchesRule(rule, url));
}

export function triggerApplies(trigger: SiteChatTrigger, url: string, online: boolean) {
    if (!trigger.enabled)
        return false;
    if (trigger.when === 'work' && !online)
        return false;
    if (trigger.when === 'off' && online)
        return false;
    if (trigger.action === 'phrase' && !trigger.text?.trim())
        return false;
    return (trigger.conditions ?? []).every(rule => matchesRule(rule, url));
}

/** A stable key for "this trigger was already shown in this browser", surviving reorders of the list. */
export function triggerKey(trigger: SiteChatTrigger) {
    const source = `${trigger.name}|${trigger.action}|${trigger.text}|${JSON.stringify(trigger.conditions)}`;
    let hash = 0;
    for (let i = 0; i < source.length; i++)
        hash = (hash * 31 + source.charCodeAt(i)) | 0;
    return `t${(hash >>> 0).toString(36)}`;
}
